import { useEffect, useState } from "react";
import { View } from "react-native";
import { Building2 } from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  SecondaryButton,
  TextActionButton,
  TextField,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";

const buildingIcon =
  require("../../../../assets/images/you-icons/building.png") as number;

type Props = {
  visible: boolean;
  onClose: () => void;
};

export default function EmployerBenefitsModal({ visible, onClose }: Props) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const updatePatient = useUpdatePatient();
  const [employerBenefit, setEmployerBenefit] = useState("");
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!visible || updatePatient.isPending) return;

    setEmployerBenefit(patient?.fertilityIntent?.employerBenefit ?? "");
    setSaveError(false);
  }, [
    patient?.fertilityIntent?.employerBenefit,
    updatePatient.isPending,
    visible,
  ]);

  const handleClose = () => {
    if (!updatePatient.isPending) onClose();
  };

  const handleSave = async () => {
    setSaveError(false);
    try {
      await updatePatient.mutateAsync({
        fertilityIntent: {
          // The API's fertility-intent upsert requires this value when the
          // relation does not yet exist. Preserve the patient's unrelated
          // fertility-intent data rather than changing medical history here.
          periodRegularity:
            patient?.fertilityIntent?.periodRegularity ?? "unsure",
          // Opening this form is the affirmative action. A user can still
          // explicitly record that they have no benefit below.
          hasEmployerBenefits: true,
          employerBenefit: employerBenefit.trim() || null,
        },
      });
      onClose();
    } catch {
      setSaveError(true);
    }
  };

  const handleNoBenefits = async () => {
    setSaveError(false);
    try {
      await updatePatient.mutateAsync({
        fertilityIntent: {
          periodRegularity:
            patient?.fertilityIntent?.periodRegularity ?? "unsure",
          hasEmployerBenefits: false,
          // An explicit No must clear a provider that may have been saved
          // earlier rather than leaving an ambiguous record behind.
          employerBenefit: null,
        },
      });
      onClose();
    } catch {
      setSaveError(true);
    }
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      dismissOnBackdropTap={!updatePatient.isPending}
      contentStyle={{
        paddingTop: 28,
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
    >
      <DialogIcon source={buildingIcon} width={52} height={62} />

      <DialogHeader
        align="center"
        title={t("you.coverage_benefits.employer_benefits_modal_title")}
        subtitle={t(
          "you.coverage_benefits.employer_benefits_modal_description",
        )}
        style={{ marginTop: 8, marginBottom: 20 }}
      />

      <TextField
        label={t("you.coverage_benefits.employer_benefits_provider_label")}
        labelHint={t("you.coverage_benefits.employer_benefits_question_hint")}
        labelIcon={Building2}
        value={employerBenefit}
        onChangeText={setEmployerBenefit}
        accessibilityLabel={t(
          "you.coverage_benefits.employer_benefits_provider_label",
        )}
        placeholder={t(
          "you.coverage_benefits.employer_benefits_provider_placeholder",
        )}
      />

      {saveError ? (
        <View style={{ marginTop: 16 }}>
          <ActionBanner
            variant="important"
            message={t("you.coverage_benefits.employer_benefits_save_failed")}
          />
        </View>
      ) : null}

      <View style={{ flexDirection: "row", gap: 10, marginTop: 22 }}>
        <SecondaryButton
          label={t("you.coverage_benefits.employer_benefits_cancel")}
          onPress={handleClose}
          disabled={updatePatient.isPending}
          flex={1}
        />
        <PrimaryButton
          label={t("you.coverage_benefits.employer_benefits_save")}
          onPress={handleSave}
          loading={updatePatient.isPending}
          flex={1}
        />
      </View>
      <TextActionButton
        label={t("you.coverage_benefits.employer_benefits_none_action")}
        onPress={handleNoBenefits}
        disabled={updatePatient.isPending}
        tone="tertiary"
        style={{ alignSelf: "center", marginTop: 4 }}
      />
    </Dialog>
  );
}
